import { FacilityState } from "./types";
import { GetFacilityResponse } from "@api/requests/facility/getFacility";
import { PostFacilityParams } from "@api/requests/facility/postFacility";
import { FacilityValues } from "@initialize/mgr/CHIIKITEICHAKU/facility/initialValues";
import {
  INT_FALSE_FROM_API,
  INT_TRUE_FROM_API,
  STRING_FALSE_FROM_API,
  STRING_TRUE_FROM_API
} from "@constants/variables";
import castString from "@utils/dataNormalizer/castString";
import { isNil } from "lodash-es";
import { selectDateValueToDate } from "@utils/date";
import castNumber from "@utils/dataNormalizer/castNumber";

/**
 * Normalized Type
 */
export type NormalizedGetFacilityResponse = FacilityState;
export type NormalizedPostFacilityParams = FacilityState;

/**
 * GetFacilityResponse === PostFacilityParams前提の共通化
 */
const normalizeApiParams = (
  result: GetFacilityResponse["data"] | PostFacilityParams
): FacilityState => {
  const { facility } = result;
  const facilityCHIIKITEICHAKU = result.facility_chiikiteichaku || {
    peer_support_flg: 0,
    life_support_hub_in_district_flg: 0
  };

  return {
    corporationName:
      facility.gov_business_owner !== null ? facility.gov_business_owner : "",
    officeNumber: !isNil(facility.gov_facility_number)
      ? facility.gov_facility_number
      : "",
    officeName: !isNil(facility.name) ? facility.name : "",
    serviceType: facility.type_service,
    representativeName: !isNil(facility.responsible_person)
      ? facility.responsible_person
      : "",
    capacity: facility.capacity !== null ? facility.capacity.toString() : "",
    postalCode: facility.postal_code !== null ? facility.postal_code : "",
    selectedPrefectureName: facility.prefecture_name
      ? facility.prefecture_name
      : "NOT_SELECTED",
    selectedCityCode:
      facility.city_id !== null ? facility.city_id.toString() : "NOT_SELECTED",
    restAddress: facility.address !== null ? facility.address : "",
    tel: !isNil(facility.tel) ? facility.tel : "",
    cityId: facility.city_id !== null ? facility.city_id.toString() : "",
    multiFunctionOfficeFlag:
      facility.multiple_facility_flg === STRING_TRUE_FROM_API,
    masterSubordinateFlg:
      facility.master_subordinate_flg === STRING_TRUE_FROM_API,
    masterFlg: facility.master_flg ? facility.master_flg : "1",
    allCapacity: castString(facility.total_capacity),
    peerSupportFlg:
      facilityCHIIKITEICHAKU.peer_support_flg === INT_TRUE_FROM_API,
    lifeSupportHubInDistrictFlg:
      facilityCHIIKITEICHAKU.life_support_hub_in_district_flg ===
      INT_TRUE_FROM_API,
    lifeSupportFunctionPromotionFlg:
      facilityCHIIKITEICHAKU.life_support_function_promotion_flg ===
      INT_TRUE_FROM_API,
    lifeSupportFunctionPromotionTimes: !isNil(
      facilityCHIIKITEICHAKU.life_support_function_promotion_times
    )
      ? facilityCHIIKITEICHAKU.life_support_function_promotion_times.toString()
      : "",
    abusePreventionMeasuresNotImplementedFlg:
      facility.abuse_prevention_measures_not_implemented_flg ===
      INT_TRUE_FROM_API,
    abusePreventionMeasuresNotImplementedFrom: facility.abuse_prevention_measures_not_implemented_from
      ? facility.abuse_prevention_measures_not_implemented_from
      : "",
    abusePreventionMeasuresNotImplementedTo: facility.abuse_prevention_measures_not_implemented_to
      ? facility.abuse_prevention_measures_not_implemented_to
      : "",
    informationDisclosureNotReportedFlg:
      facility.information_disclosure_not_reported_flg === INT_TRUE_FROM_API,
    informationDisclosureNotReportedFrom: facility.information_disclosure_not_reported_from
      ? facility.information_disclosure_not_reported_from
      : "",
    informationDisclosureNotReportedTo: facility.information_disclosure_not_reported_to
      ? facility.information_disclosure_not_reported_to
      : ""
  };
};

export const normalizeGetFacilityResult = (
  response: GetFacilityResponse
): NormalizedGetFacilityResponse => normalizeApiParams(response.data);

export const normalizePostFacilityParams = (
  params: PostFacilityParams
): NormalizedPostFacilityParams => normalizeApiParams(params);

export const removeNoChangeData = (
  after: PostFacilityParams,
  before: PostFacilityParams
): PostFacilityParams => {
  const target: PostFacilityParams = {
    facility: {
      gov_business_owner: after.facility.gov_business_owner,
      gov_facility_number: after.facility.gov_facility_number,
      name: after.facility.name,
      type_service: after.facility.type_service,
      responsible_person: after.facility.responsible_person,
      capacity: after.facility.capacity,
      postal_code: after.facility.postal_code,
      prefecture_name: after.facility.prefecture_name,
      city_id: after.facility.city_id,
      address: after.facility.address,
      tel: after.facility.tel,
      multiple_facility_flg: after.facility.multiple_facility_flg,
      users_vs_supporter_grade: after.facility.users_vs_supporter_grade,
      welfare_condition: after.facility.welfare_condition,
      welfare_condition_start_date: after.facility.welfare_condition_start_date,
      welfare_condition_end_date: after.facility.welfare_condition_end_date,
      better_supporter_condition: after.facility.better_supporter_condition,
      lack_of_supporter_flg: after.facility.lack_of_supporter_flg,
      date_start_lack_of_supporter: after.facility.date_start_lack_of_supporter,
      lack_of_service_admin_flg: after.facility.lack_of_service_admin_flg,
      date_start_lack_of_service_admin:
        after.facility.date_start_lack_of_service_admin,
      origin_local_gov_flg: after.facility.origin_local_gov_flg,
      see_hear_team_flg: after.facility.see_hear_team_flg,
      total_capacity: after.facility.total_capacity,
      abuse_prevention_measures_not_implemented_flg: undefined,
      abuse_prevention_measures_not_implemented_from: undefined,
      abuse_prevention_measures_not_implemented_to: undefined,
      information_disclosure_not_reported_flg: undefined,
      information_disclosure_not_reported_from: undefined,
      information_disclosure_not_reported_to: undefined
    },
    facility_chiikiteichaku: {
      peer_support_flg: undefined,
      life_support_hub_in_district_flg: undefined,
      life_support_function_promotion_flg: undefined,
      life_support_function_promotion_times: undefined
    }
  };

  Object.keys(target).forEach((key) => {
    Object.keys(target[key]).forEach((param) => {
      if (target[key][param] === undefined) {
        if (`${before[key][param]}` !== `${after[key][param]}`) {
          target[key][param] = after[key][param];
        } else {
          target[key][param] = undefined;
        }
      }
    });
  });

  // 虐待防止措置未実施減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.abuse_prevention_measures_not_implemented_flg !==
      undefined ||
    target.facility.abuse_prevention_measures_not_implemented_from !==
      undefined ||
    target.facility.abuse_prevention_measures_not_implemented_to !== undefined
  ) {
    target.facility.abuse_prevention_measures_not_implemented_flg =
      after.facility.abuse_prevention_measures_not_implemented_flg;
    target.facility.abuse_prevention_measures_not_implemented_from =
      after.facility.abuse_prevention_measures_not_implemented_from;
    target.facility.abuse_prevention_measures_not_implemented_to =
      after.facility.abuse_prevention_measures_not_implemented_to;
  }

  // 情報公表未報告減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.information_disclosure_not_reported_flg !== undefined ||
    target.facility.information_disclosure_not_reported_from !== undefined ||
    target.facility.information_disclosure_not_reported_to !== undefined
  ) {
    target.facility.information_disclosure_not_reported_flg =
      after.facility.information_disclosure_not_reported_flg;
    target.facility.information_disclosure_not_reported_from =
      after.facility.information_disclosure_not_reported_from;
    target.facility.information_disclosure_not_reported_to =
      after.facility.information_disclosure_not_reported_to;
  }

  if (target.facility_chiikiteichaku && after.facility_chiikiteichaku) {
    // 地域生活支援拠点等機能強化加算と算定回数はセットで送信する
    if (
      target.facility_chiikiteichaku.life_support_function_promotion_flg ||
      target.facility_chiikiteichaku.life_support_function_promotion_times
    ) {
      target.facility_chiikiteichaku.life_support_function_promotion_flg =
        after.facility_chiikiteichaku.life_support_function_promotion_flg;
      target.facility_chiikiteichaku.life_support_function_promotion_times =
        after.facility_chiikiteichaku.life_support_function_promotion_times;
    }
  }

  return target;
};

export const normalizeFormValue = (
  state: FacilityValues
): PostFacilityParams => {
  return {
    facility: {
      gov_business_owner: state.basic.corporationName,
      gov_facility_number: state.basic.officeNumber,
      name: state.basic.officeName,
      type_service: state.basic.serviceType,
      responsible_person: state.basic.representativeName,
      capacity:
        state.basic.capacity !== "" ? parseInt(state.basic.capacity, 10) : null,
      postal_code: state.basic.postalCode,
      prefecture_name: state.basic.prefectureId,
      city_id: parseInt(state.basic.cityId, 10),
      address: state.basic.restAddress,
      tel: state.basic.tel,
      multiple_facility_flg: state.basic.multiFunctionOfficeFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      users_vs_supporter_grade: "",
      welfare_condition: "",
      welfare_condition_start_date: null,
      welfare_condition_end_date: null,
      better_supporter_condition: "",
      lack_of_supporter_flg: STRING_FALSE_FROM_API,
      date_start_lack_of_supporter: null,
      lack_of_service_admin_flg: STRING_FALSE_FROM_API,
      date_start_lack_of_service_admin: null,
      origin_local_gov_flg: STRING_FALSE_FROM_API,
      see_hear_team_flg: STRING_FALSE_FROM_API,
      total_capacity:
        state.basic.multiFunctionOfficeFlag && state.basic.allCapacity !== ""
          ? parseInt(state.basic.allCapacity, 10)
          : null,

      abuse_prevention_measures_not_implemented_flg: state.subtractionItem
        .abusePreventionMeasuresNotImplementedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      abuse_prevention_measures_not_implemented_from:
        state.subtractionItem.abusePreventionMeasuresNotImplementedFrom &&
        selectDateValueToDate(
          state.subtractionItem.abusePreventionMeasuresNotImplementedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.abusePreventionMeasuresNotImplementedFrom
            )
          : null,
      abuse_prevention_measures_not_implemented_to:
        state.subtractionItem.abusePreventionMeasuresNotImplementedTo &&
        selectDateValueToDate(
          state.subtractionItem.abusePreventionMeasuresNotImplementedTo
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.abusePreventionMeasuresNotImplementedTo
            )
          : null,
      information_disclosure_not_reported_flg: state.subtractionItem
        .informationDisclosureNotReportedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      information_disclosure_not_reported_from:
        state.subtractionItem.informationDisclosureNotReportedFrom &&
        selectDateValueToDate(
          state.subtractionItem.informationDisclosureNotReportedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.informationDisclosureNotReportedFrom
            )
          : null,
      information_disclosure_not_reported_to:
        state.subtractionItem.informationDisclosureNotReportedTo &&
        selectDateValueToDate(
          state.subtractionItem.informationDisclosureNotReportedTo
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.informationDisclosureNotReportedTo
            )
          : null
    },
    facility_chiikiteichaku: {
      peer_support_flg: state.additionalItem.peerSupportFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_hub_in_district_flg: state.additionalItem
        .lifeSupportHubInDistrictFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_flg: state.additionalItem
        .lifeSupportFunctionPromotionFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_times:
        state.additionalItem.lifeSupportHubInDistrictFlg &&
        state.additionalItem.lifeSupportFunctionPromotionTimes
          ? castNumber(state.additionalItem.lifeSupportFunctionPromotionTimes)
          : null
    }
  };
};

export const normalizePostDifferenceParams = (
  params: PostFacilityParams,
  response: FacilityValues
): PostFacilityParams => {
  return removeNoChangeData(params, normalizeFormValue(response));
};
